// Admin transport reuses the dashboard's access token and the teacher's
// streaming protocol. Teacher/student requests keep their existing clients.
import { API_STAGE } from '../../config/api';

// Streaming can't ride the 60bjnqzpe4 gateway, so each claude-sandy-admin
// alias has its own function URL (2026-09-24). Same stage switch as
// config/api.js: npm start -> dev ($LATEST), Amplify build -> prod (pinned).
const ADMIN_SANDY_URLS = {
  Development: 'https://ayohajfpwbjomh2dk6aj5aucwe0ecbgz.lambda-url.us-east-1.on.aws/',
  Production: 'https://nvlzk5chkmwsaxmieotxfbvqqa0ifzfy.lambda-url.us-east-1.on.aws/',
};
export const ADMIN_SANDY_URL = process.env.REACT_APP_SANDY_ADMIN_URL
  || ADMIN_SANDY_URLS[API_STAGE];
const tokenOf = auth => auth?.user?.accessToken;
// Chat limits (2026-10-07): the admin's newest message may hold a pasted list
// of about 500 lines; earlier messages are sent shortened, as Buffy keeps them.
export const ADMIN_CHAT_MAX_CHARS = 20000;
export const ADMIN_CHAT_LIMIT_MORE = 'For a longer list, use Accommodations › “Compare your list”.';
const EARLIER_MAX_CHARS = 4000;
// The admin's time zone (2026-10-07), as the teacher assistant sends it: Buffy
// says when a day's counts end in local time instead of guessing a zone.
const browserTz = () => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined; } catch { return undefined; } };
const freshToken = fallback => {
  try { return JSON.parse(localStorage.getItem('sessionToken'))?.accessToken || fallback; }
  catch { return fallback; }
};
async function post(auth, body, signal) {
  if (!ADMIN_SANDY_URL) throw Object.assign(new Error('Admin assistant is not connected yet.'), { code: 'not_connected' });
  let token = tokenOf(auth);
  if (!token) throw Object.assign(new Error('Sign in to use Buffy.'), { status: 401 });
  const send = () => fetch(ADMIN_SANDY_URL, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: token }, body: JSON.stringify(body), signal });
  let response = await send();
  if ((response.status === 401 || response.status === 403) && auth?.refreshToken) {
    await auth.refreshToken(); token = freshToken(token); response = await send();
  }
  if (!response.ok) throw Object.assign(new Error(`Admin assistant request failed (${response.status}).`), { status: response.status });
  return response;
}
export async function streamAdminSandy({ auth, messages, page, convoId, viewContext, goalPlan, pageData, signal, onDelta, onStatus, onTag }) {
  // No teacher screen store, seed, class controls or action capabilities are
  // forwarded across surfaces. School scope comes from the authenticated API.
  const history = messages.slice(-30).map((m, i, all) => (i < all.length - 1 && typeof m.content === 'string' && m.content.length > EARLIER_MAX_CHARS
    ? { ...m, content: `${m.content.slice(0, EARLIER_MAX_CHARS)}

[Earlier message shortened to 4,000 characters.]` } : m));
  while (history.length && history[0].role !== 'user') history.shift();
  // ui 'directory' (2026-09-28): this dashboard has the Students, Teachers, Levels,
  // Usage and Accommodations pages, so Buffy uses their guide and page tools.
  // pageData (2026-10-08): what the page shows; only pages that register it send it.
  const response = await post(auth, { surface: 'admin-real', ui: 'directory', messages: history, page, convoId, tz: browserTz(), ...(viewContext ? {viewContext} : {}), ...(goalPlan ? {goalPlan} : {}), ...(pageData ? {pageData} : {}) }, signal);
  if (!response.body) throw new Error('The response stream was unavailable.');
  const reader = response.body.getReader(), decoder = new TextDecoder();
  let buffer = '', done = null, pendingText = '';
  const emitText = (text, flush = false) => {
    pendingText += text;
    // Only harmless follow-up choices are supported on the admin surface. A
    // choice over 80 characters is too long for a button: dropped, never shown as text.
    for (;;) {
      const match = /<option\s+text="([^"<>]{1,200})"\s*\/>/.exec(pendingText);
      if (!match) break;
      onDelta?.(pendingText.slice(0, match.index));
      if (match[1].length <= 80) onTag?.({ kind: 'option', text: match[1] });
      pendingText = pendingText.slice(match.index + match[0].length);
    }
    const last = pendingText.lastIndexOf('<');
    if (!flush && last >= 0 && pendingText.length - last < 400) {
      onDelta?.(pendingText.slice(0, last)); pendingText = pendingText.slice(last);
    } else { if (pendingText) onDelta?.(pendingText); pendingText = ''; }
  };
  const consume = line => {
    if (!line.trim()) return;
    let value; try { value = JSON.parse(line); } catch { throw new Error('Invalid response stream.'); }
    if (value.type === 'delta') emitText(value.text || '');
    else if (value.type === 'status') onStatus?.(value);
    else if (value.type === 'done') done = value;
    else if (value.type === 'error') throw Object.assign(new Error(value.code || 'Admin assistant unavailable.'), { code: value.code });
  };
  try {
    for (;;) {
      const chunk = await reader.read();
      if (chunk.done) break;
      buffer += decoder.decode(chunk.value, { stream: true });
      let end;
      while ((end = buffer.indexOf('\n')) >= 0) { consume(buffer.slice(0, end)); buffer = buffer.slice(end + 1); }
    }
    buffer += decoder.decode();
    if (buffer.trim()) consume(buffer);
    emitText('', true);
    return done;
  } finally { reader.releaseLock(); }
}
export function warmAdminSandy(auth) {
  if (ADMIN_SANDY_URL && tokenOf(auth)) post(auth, { surface: 'admin-real', ui: 'directory', warm: true }).catch(() => {});
}
